import { logger } from 'monitor';

import { SopModes } from '../../../components/BaleAnalysisContext/BaleAnalysisContext';
import { PhotoRejectionReason } from '../../../types';
import { isAspectRatioValid } from '../../isAspectRatioValid/isAspectRatioValid';
import { isFileDuplicated } from '../isFileDuplicated/isFileDuplicated';
import { isFileEmpty } from '../isFileEmpty/isFileEmpty';
import { isFileTypeValid } from '../isFileTypeValid/isFileTypeValid';

export type FileValidationResult = {
  isValid: boolean;
  rejectionReason?: PhotoRejectionReason;
};

export type ValidateFileOptions = {
  existingFiles?: File[];
  logContext?: Record<string, unknown>;
  sopMode?: SopModes;
};

export const MIN_REGULAR_ASPECT_RATIO = 3 / 4;
const MIN_EXTENDED_ASPECT_RATIO = 9 / 16;
export const validateFile = async (file: File, options: ValidateFileOptions = {}): Promise<FileValidationResult> => {
  const { existingFiles, logContext = {}, sopMode } = options;
  const context = { fileName: file.name, ...logContext };

  if (isFileEmpty(file)) {
    logger.warn('empty file detected', context);
    return {
      isValid: false,
      rejectionReason: PhotoRejectionReason.EMPTY_FILE,
    };
  }

  if (!isFileTypeValid(file)) {
    logger.warn('invalid file type detected', { ...context, type: file.type });
    return {
      isValid: false,
      rejectionReason: PhotoRejectionReason.INVALID_FORMAT,
    };
  }

  if (existingFiles && existingFiles.length > 0 && (await isFileDuplicated(existingFiles, file))) {
    logger.warn('duplicate photo detected', context);
    return {
      isValid: false,
      rejectionReason: PhotoRejectionReason.DUPLICATE,
    };
  }

  const isAspectRatioValidResult = await isAspectRatioValid(
    file,
    sopMode === 'extended' ? MIN_EXTENDED_ASPECT_RATIO : MIN_REGULAR_ASPECT_RATIO
  );
  if (!isAspectRatioValidResult) {
    logger.warn('invalid aspect ratio detected', context);
    return {
      isValid: false,
      rejectionReason:
        sopMode === 'extended'
          ? PhotoRejectionReason.BAD_ASPECT_RATIO_EXTENDED
          : PhotoRejectionReason.BAD_ASPECT_RATIO_REGULAR,
    };
  }

  return { isValid: true };
};
